// src/functions/apply-listing-sort-order.function.ts
import { ListingSortOrder } from "@polonia-medica/types";
function applyListingSortOrder(qb, sort, entityAlias) {
  if (sort === ListingSortOrder.DEFAULT) {
    qb.orderBy(`${entityAlias}.featured`, "DESC").addOrderBy("RANDOM()");
    return qb;
  }
  switch (sort) {
    case ListingSortOrder.NEWEST:
      qb.orderBy(`${entityAlias}.createdAt`, "DESC");
      break;
    case ListingSortOrder.OLDEST:
      qb.orderBy(`${entityAlias}.createdAt`, "ASC");
      break;
    case ListingSortOrder.NAME_DESC:
      qb.orderBy("translation.name", "DESC");
      break;
    case ListingSortOrder.RANDOM:
      qb.orderBy("RANDOM()");
      break;
    case ListingSortOrder.NAME_ASC:
    default:
      qb.orderBy("translation.name", "ASC");
      break;
  }
  return qb;
}

// src/functions/build-weighted-fuzzy-search.function.ts
function buildWeightedFuzzySearch(weightedFields, search, options) {
  const shortQueryLength = options?.shortQueryLength ?? 3;
  const minSimilarity = options?.minSimilarity ?? 0.08;
  const prefix = options?.paramPrefix ?? "fs";
  const primaryNameField = options?.primaryNameField;
  const exactMatchBonus = options?.exactMatchBonus ?? 50;
  const startsWithBonus = options?.startsWithBonus ?? 30;
  const containsBonus = options?.containsBonus ?? 20;
  const normalizeField = (field) => {
    return `COALESCE(LOWER(${field}), '')`;
  };
  let scoreExpr = weightedFields.map(({ field, weight }) => `${weight} * similarity(${normalizeField(field)}, LOWER(:${prefix}_search))`).join(" + ");
  if (primaryNameField) {
    const normalizedPrimaryField = normalizeField(primaryNameField);
    const exactMatch = `${normalizedPrimaryField} = LOWER(:${prefix}_search)`;
    const startsWith = `${normalizedPrimaryField} LIKE LOWER(:${prefix}_search) || '%'`;
    const contains = `${normalizedPrimaryField} LIKE '%' || LOWER(:${prefix}_search) || '%'`;
    scoreExpr = `(${scoreExpr}) + CASE
      WHEN ${exactMatch} THEN ${exactMatchBonus}
      WHEN ${startsWith} THEN ${startsWithBonus}
      WHEN ${contains} THEN ${containsBonus}
      ELSE 0
    END`;
  }
  if (search.length < shortQueryLength) {
    const whereSql2 = weightedFields.map(({ field }) => `${normalizeField(field)} ILIKE :${prefix}_like`).join(" OR ");
    return {
      whereSql: whereSql2,
      // Provide both :like and :search so scoreExpr can consistently reference :search
      params: { [`${prefix}_like`]: `%${search}%`, [`${prefix}_search`]: search },
      scoreExpr
    };
  }
  const whereSql = weightedFields.map(({ field }) => `similarity(${normalizeField(field)}, LOWER(:${prefix}_search)) > :${prefix}_minSim`).join(" OR ");
  return {
    whereSql,
    params: { [`${prefix}_search`]: search, [`${prefix}_minSim`]: minSimilarity },
    scoreExpr
  };
}

// src/functions/ipx-validate-modifiers.function.ts
import { ErrorCode } from "@polonia-medica/errors";
var ALLOWED_FORMATS = /* @__PURE__ */ new Set(["jpg", "jpeg", "png", "webp", "avif", "gif", "heif", "tiff", "auto"]);
var ALLOWED_FIT = /* @__PURE__ */ new Set(["cover", "contain", "fill", "inside", "outside"]);
var ALLOWED_POSITIONS = /* @__PURE__ */ new Set([
  "top",
  "bottom",
  "left",
  "right",
  "center",
  "centre",
  "north",
  "south",
  "east",
  "west",
  "northeast",
  "northwest",
  "southeast",
  "southwest",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right"
]);
function isValidHexColor(color) {
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(color);
}
function ipxValidateModifiers(modifiers) {
  if (!modifiers || modifiers === "" || modifiers === "_") return { success: true, sanitized: "_" };
  const parts = modifiers.split(",").filter(Boolean);
  const sanitized = [];
  for (const part of parts) {
    const [key, raw] = part.split("_", 2);
    if (!key || !raw) return { success: false, code: ErrorCode.ERR_IPX_BAD_MODIFIER };
    if (key === "w" || key === "width" || key === "h" || key === "height") {
      const v = Number(raw);
      if (!Number.isInteger(v) || v < 1 || v > 4e3) return { success: false, code: ErrorCode.ERR_IPX_BAD_DIM };
      const normalizedKey = key === "width" ? "w" : key === "height" ? "h" : key;
      sanitized.push(`${normalizedKey}_${v}`);
      continue;
    }
    if (key === "s" || key === "resize") {
      const dimensionsMatch = raw.match(/^(\d+)x(\d+)$/);
      if (!dimensionsMatch) return { success: false, code: ErrorCode.ERR_IPX_BAD_DIM };
      const [, width, height] = dimensionsMatch;
      const w = Number(width);
      const h = Number(height);
      if (w < 1 || w > 4e3 || h < 1 || h > 4e3) return { success: false, code: ErrorCode.ERR_IPX_BAD_DIM };
      sanitized.push(`s_${w}x${h}`);
      continue;
    }
    if (key === "q" || key === "quality") {
      const v = Number(raw);
      if (!Number.isInteger(v) || v < 30 || v > 95) return { success: false, code: ErrorCode.ERR_IPX_BAD_QUALITY };
      sanitized.push(`q_${v}`);
      continue;
    }
    if (key === "f" || key === "format") {
      if (!ALLOWED_FORMATS.has(raw)) return { success: false, code: ErrorCode.ERR_IPX_BAD_FORMAT };
      sanitized.push(`f_${raw}`);
      continue;
    }
    if (key === "fit") {
      if (!ALLOWED_FIT.has(raw)) return { success: false, code: ErrorCode.ERR_IPX_BAD_FIT };
      sanitized.push(`fit_${raw}`);
      continue;
    }
    if (key === "pos" || key === "position") {
      if (!ALLOWED_POSITIONS.has(raw)) return { success: false, code: ErrorCode.ERR_IPX_BAD_POSITION };
      sanitized.push(`pos_${raw}`);
      continue;
    }
    if (key === "b" || key === "background") {
      if (!isValidHexColor(raw)) return { success: false, code: ErrorCode.ERR_IPX_BAD_BACKGROUND };
      sanitized.push(`b_${raw}`);
      continue;
    }
    return { success: false, code: ErrorCode.ERR_IPX_MODIFIER_NOT_ALLOWED };
  }
  return { success: true, sanitized: sanitized.join(",") };
}

// src/functions/normalize-llm-prose-formatting.function.ts
function normalizeLlmProseFormatting(content) {
  if (!content) return content;
  let s = content;
  s = fixMarkdownUnorderedListMarkerSpacing(s);
  s = fixOrderedListMarkerSpacing(s);
  s = fixRepeatedOrderedListNumbering(s);
  s = fixCollapsedPostalCodeSpacing(s);
  return s;
}
function fixMarkdownUnorderedListMarkerSpacing(content) {
  return content.replace(/^(\s*)([-*+])(?![\s-*+])(\S.*)$/gm, "$1$2 $3");
}
function fixOrderedListMarkerSpacing(content) {
  return content.replace(/^(\s*)(\d+)\.(?=[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż])/gm, "$1$2. ");
}
function fixRepeatedOrderedListNumbering(content) {
  const lines = content.split("\n");
  const result = [];
  let listCounter = 0;
  let listIndent = null;
  const orderedRe = /^(\s*)(\d+)\.\s+(.*)$/;
  for (const line of lines) {
    const ordered = line.match(orderedRe);
    if (ordered) {
      const indent = ordered[1];
      const rest = ordered[3];
      if (listIndent === null || indent === listIndent) {
        listIndent = indent;
        listCounter += 1;
        result.push(`${indent}${listCounter}. ${rest}`);
        continue;
      }
      listIndent = indent;
      listCounter = 1;
      result.push(`${indent}${listCounter}. ${rest}`);
      continue;
    }
    if (listIndent !== null) {
      const bullet = line.match(/^(\s+)([-*+])\s+/);
      if (bullet && bullet[1].length > listIndent.length) {
        result.push(line);
        continue;
      }
    }
    listCounter = 0;
    listIndent = null;
    result.push(line);
  }
  return result.join("\n");
}
function fixCollapsedPostalCodeSpacing(content) {
  return content.replace(/(\d{2}-\d{3})(?=[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż])/g, "$1 ").replace(/,(\d{2}-\d{3})\b/g, ", $1");
}

// src/functions/seed-to-float.function.ts
function seedToFloat(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 1e4 / 1e4 * 2 - 1;
}

// src/functions/slugify.function.ts
import slugifyLib from "slugify";
var defaultSlugifyOptions = {
  replacement: "-",
  lower: true,
  strict: true,
  trim: true
};
function slugify(text, options) {
  const mergedOptions = { ...defaultSlugifyOptions, ...options };
  return slugifyLib(text, mergedOptions);
}

// src/functions/travel-form-companion-seed.function.ts
var COMPANION_IDENTITY_FIELD_KEYS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "dateOfBirth"
];
var cloneValue = (value) => {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
};
var omitAdditionalStaysFromCompanionSections = (itemSections) => {
  return (itemSections ?? []).map((section) => {
    if (section.key !== "accommodation") {
      return section;
    }
    return {
      ...section,
      fields: (section.fields ?? []).filter((field) => field.key !== "additionalStays")
    };
  });
};
var seedCompanionItemFromTraveler = (travelerPayload, itemSections) => {
  const seeded = {};
  for (const section of itemSections ?? []) {
    const source = travelerPayload[section.key];
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      continue;
    }
    const cloned = cloneValue(source);
    if (section.key === "basic") {
      for (const key of COMPANION_IDENTITY_FIELD_KEYS) {
        delete cloned[key];
      }
    }
    if (section.key === "accommodation") {
      delete cloned["additionalStays"];
    }
    seeded[section.key] = cloned;
  }
  return seeded;
};

// src/functions/travel-form-visibility.function.ts
var asRules = (rules) => {
  if (!rules) return [];
  return Array.isArray(rules) ? rules : [rules];
};
var resolveVisibilityFieldValue = (fieldPath, payload, sectionKey) => {
  const normalized = fieldPath.trim();
  if (!normalized) return void 0;
  if (normalized.includes(".")) {
    const [section, ...rest] = normalized.split(".");
    const fieldKey = rest.join(".");
    const sectionPayload = payload[section];
    if (!sectionPayload || typeof sectionPayload !== "object" || Array.isArray(sectionPayload)) {
      return void 0;
    }
    return sectionPayload[fieldKey];
  }
  if (sectionKey) {
    const sectionPayload = payload[sectionKey];
    if (sectionPayload && typeof sectionPayload === "object" && !Array.isArray(sectionPayload)) {
      return sectionPayload[normalized];
    }
  }
  return payload[normalized];
};
var matchesEquals = (value, expected) => {
  if (Array.isArray(value)) {
    return value.length === 1 && String(value[0]) === String(expected);
  }
  if (typeof expected === "boolean") {
    return value === expected;
  }
  return value === expected || String(value) === String(expected);
};
var matchesVisibilityRule = (rule, payload, sectionKey) => {
  const value = resolveVisibilityFieldValue(rule.field, payload, sectionKey);
  if (rule.equals !== void 0 && !matchesEquals(value, rule.equals)) {
    return false;
  }
  if (rule.notEquals !== void 0 && matchesEquals(value, rule.notEquals)) {
    return false;
  }
  if (rule.includes !== void 0) {
    if (!Array.isArray(value) || !value.map(String).includes(String(rule.includes))) {
      return false;
    }
  }
  if (rule.notIncludes !== void 0) {
    if (Array.isArray(value) && value.map(String).includes(String(rule.notIncludes))) {
      return false;
    }
  }
  return true;
};
var isVisible = (rules, payload, sectionKey) => {
  const list = asRules(rules);
  if (list.length === 0) return true;
  return list.every((rule) => matchesVisibilityRule(rule, payload, sectionKey));
};
var isSectionVisible = (section, payload) => {
  return isVisible(section.visibleWhen, payload, section.key);
};
var isFieldVisible = (field, payload, sectionKey, options = {}) => {
  if (options.sectionVisible === false) return false;
  return isVisible(field.visibleWhen, payload, sectionKey);
};
var isFieldEffectivelyRequired = (field, payload, sectionKey) => {
  if (!field.required) return false;
  return isFieldVisible(field, payload, sectionKey);
};
var isEmptyValue = (value) => {
  if (value === null || value === void 0 || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    const record = value;
    if ("start" in record || "end" in record) {
      return !record["start"] && !record["end"];
    }
  }
  return false;
};
var sanitizePayload = (snapshot, payload) => {
  const result = {};
  for (const section of snapshot.sections ?? []) {
    if (!isSectionVisible(section, payload)) {
      continue;
    }
    const rawSection = payload[section.key];
    const sectionPayload = rawSection && typeof rawSection === "object" && !Array.isArray(rawSection) ? rawSection : {};
    const cleanedSection = {};
    for (const field of section.fields ?? []) {
      if (!isFieldVisible(field, payload, section.key)) {
        continue;
      }
      const value = sectionPayload[field.key];
      if (value === void 0) {
        continue;
      }
      if (field.type === "repeater") {
        cleanedSection[field.key] = sanitizeRepeaterItems(field, value);
        continue;
      }
      cleanedSection[field.key] = value;
    }
    if (Object.keys(cleanedSection).length > 0 || section.fields.length === 0) {
      result[section.key] = cleanedSection;
    }
  }
  return result;
};
var sanitizeRepeaterItems = (field, value) => {
  if (!Array.isArray(value)) return [];
  const itemSnapshot = { sections: field.itemSections ?? [] };
  return value.filter((item) => !!item && typeof item === "object" && !Array.isArray(item)).map((item) => sanitizePayload(itemSnapshot, item));
};
var areVisibleRequiredFieldsFilled = (sections, payload) => {
  for (const section of sections ?? []) {
    if (!isSectionVisible(section, payload)) continue;
    for (const field of section.fields ?? []) {
      if (!isFieldEffectivelyRequired(field, payload, section.key)) continue;
      const sectionPayload = payload[section.key] && typeof payload[section.key] === "object" && !Array.isArray(payload[section.key]) ? payload[section.key] : {};
      const value = sectionPayload[field.key];
      if (field.type === "boolean") {
        if (value !== true) return false;
        continue;
      }
      if (field.type === "date-range") {
        const range = value;
        if (!range?.start || !range?.end) return false;
        continue;
      }
      if (field.type === "repeater") {
        const items = Array.isArray(value) ? value : [];
        const minItems = field.minItems ?? 0;
        if (items.length < minItems) return false;
        for (const item of items) {
          if (!item || typeof item !== "object" || Array.isArray(item)) return false;
          if (!areVisibleRequiredFieldsFilled(field.itemSections ?? [], item)) {
            return false;
          }
        }
        continue;
      }
      if (isEmptyValue(value)) return false;
    }
  }
  return true;
};

// src/functions/weighted-fuzzy-search.typeorm.ts
function applyWeightedFuzzySearch(config) {
  const { qb, weightedFields, search, options, subQuery, scoreAlias = "search_score" } = config;
  if (!search || search.length === 0 || weightedFields.length === 0) return;
  const { whereSql, params, scoreExpr } = buildWeightedFuzzySearch(weightedFields, search, options);
  const existsSub = qb.subQuery().select("1").from(subQuery.from.entity, subQuery.from.alias).where(`(${whereSql})`, params);
  for (const join of subQuery.joins) {
    const method = (join.type ?? "left") === "left" ? "leftJoin" : "innerJoin";
    existsSub[method](`${join.fromAlias}.${join.relation}`, join.alias);
  }
  existsSub.andWhere(`${subQuery.subIdColumn} = ${subQuery.rootIdColumn}`);
  qb.andWhere(`EXISTS (${existsSub.getQuery()})`);
  qb.leftJoin(
    (sq) => {
      const s = sq.subQuery().select(`${subQuery.subIdColumn}`, "id").addSelect(`(${scoreExpr})`, scoreAlias).from(subQuery.from.entity, subQuery.from.alias).where(`(${whereSql})`, params);
      for (const join of subQuery.joins) {
        const method = (join.type ?? "left") === "left" ? "leftJoin" : "innerJoin";
        s[method](`${join.fromAlias}.${join.relation}`, join.alias);
      }
      return s;
    },
    "fuzzy_sq",
    `fuzzy_sq.id = ${subQuery.rootIdColumn}`
  );
  qb.addSelect(`fuzzy_sq.${scoreAlias}`, scoreAlias);
  qb.addOrderBy(scoreAlias, "DESC");
}
function applyWeightedFuzzySearchWithPaths(config) {
  const { qb, root, joinPaths, weightedFields, search, options, scoreAlias } = config;
  const subAlias = `${root.alias}_sub`;
  const joins = joinPaths.map((jp) => {
    const [lhs, aliasPart] = jp.split(/\s+as\s+/i);
    const alias = (aliasPart || "").trim();
    const lhsTrim = lhs.trim();
    if (lhsTrim.includes(".")) {
      const [fromAlias, relation] = lhsTrim.split(".", 2);
      return { fromAlias: fromAlias.trim(), relation: relation.trim(), alias, type: "left" };
    }
    return { fromAlias: subAlias, relation: lhsTrim, alias, type: "left" };
  }).map((j) => ({ ...j, fromAlias: j.fromAlias === root.alias ? subAlias : j.fromAlias }));
  applyWeightedFuzzySearch({
    qb,
    weightedFields,
    search,
    options,
    subQuery: {
      from: { entity: root.entity, alias: subAlias },
      joins,
      rootIdColumn: `${root.alias}.${root.idColumn ?? "id"}`,
      subIdColumn: `${subAlias}.${root.idColumn ?? "id"}`
    },
    scoreAlias
  });
}

// src/transformers/daterange.transformer.ts
var daterangeTransformer = {
  to: (value) => {
    if (!value || !Array.isArray(value) || value.length !== 2) return null;
    const [start, end] = value;
    return `[${start},${end}]`;
  },
  from: (value) => {
    if (!value) return null;
    const match = value.match(/\[([^,]+),([^\])]+)[\])]/);
    if (!match) return null;
    return [match[1], match[2]];
  }
};

// src/transformers/decimal.transformer.ts
var decimalTransformer = {
  to: (value) => value === void 0 ? null : value,
  from: (value) => value !== null && value !== void 0 ? Number(value) : null
};

// src/transformers/phone-number.transformer.ts
import { Transform } from "class-transformer";
import { parsePhoneNumberFromString } from "libphonenumber-js";
var PhoneNumberTransform = (options = {}) => Transform(({ value, obj }) => {
  if (value === null || value === void 0 || value === "") {
    return null;
  }
  let raw = String(value).trim();
  raw = raw.replace(/(?!^\+)[^\d]/g, "");
  const countryOfOrigin = obj?.countryOfOrigin?.toUpperCase();
  const effectiveDefaultCountry = options.defaultCountry ?? "PL";
  const phoneNumber = parsePhoneNumberFromString(raw, countryOfOrigin ?? effectiveDefaultCountry) ?? parsePhoneNumberFromString(raw);
  if (!phoneNumber || !phoneNumber.isValid()) {
    return raw;
  }
  return phoneNumber.number;
});

// src/transformers/sanitizeInput.transformer.ts
function sanitizeInput(input) {
  if (!input) return input;
  return input.trim().replace(/[<>\"']/g, "").replace(/javascript:/gi, "").replace(/on\w+=/gi, "").replace(/<script[\s\S]*?<\/script>/gi, "").replace(/&/g, "&amp;").replace(/\s{2,}/g, " ");
}

// src/validation/password.ts
var PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\p{L}\p{N}\s])[\S]{12,}$/u;
var PASSWORD_REPEATED_CHARS_REGEX = /(.)\1{2,}/u;
var PASSWORD_NO_REPEATED_CHARS_REGEX = /^(?!.*(.)\1{2})[\S]+$/u;
function isPasswordValid(password) {
  return PASSWORD_REGEX.test(password) && !PASSWORD_REPEATED_CHARS_REGEX.test(password);
}
function hasRepeatedChars(password) {
  return PASSWORD_REPEATED_CHARS_REGEX.test(password);
}
export {
  COMPANION_IDENTITY_FIELD_KEYS,
  PASSWORD_NO_REPEATED_CHARS_REGEX,
  PASSWORD_REGEX,
  PASSWORD_REPEATED_CHARS_REGEX,
  PhoneNumberTransform,
  applyListingSortOrder,
  applyWeightedFuzzySearch,
  applyWeightedFuzzySearchWithPaths,
  areVisibleRequiredFieldsFilled,
  buildWeightedFuzzySearch as buildWeightedFuzzyFragments,
  buildWeightedFuzzySearch,
  daterangeTransformer,
  decimalTransformer,
  defaultSlugifyOptions,
  fixCollapsedPostalCodeSpacing,
  fixMarkdownUnorderedListMarkerSpacing,
  fixOrderedListMarkerSpacing,
  fixRepeatedOrderedListNumbering,
  hasRepeatedChars,
  ipxValidateModifiers,
  isFieldEffectivelyRequired,
  isFieldVisible,
  isPasswordValid,
  isSectionVisible,
  isVisible,
  matchesVisibilityRule,
  normalizeLlmProseFormatting,
  omitAdditionalStaysFromCompanionSections,
  resolveVisibilityFieldValue,
  sanitizeInput,
  sanitizePayload,
  seedCompanionItemFromTraveler,
  seedToFloat,
  slugify
};
